import { api } from './client';

export interface AdTagOption {
  value: string;
  label: string;
  sort_order: number;
}

export interface MetricCondition {
  metric: string;
  operator: string;
  value: number;
  value_max?: number;
  window?: string;
  reference?: string;
  reference_metric?: string;
}

export interface ObjectEvidence {
  object_level: string;
  object_id?: string;
  object_name?: string;
  contribution_pct?: number;
  metric_facts?: Record<string, number>;
  tags?: Record<string, string>;
}

export interface ExperienceEvidence {
  diagnostic_tags: string[];
  metric_facts: Record<string, number>;
  object_evidence: ObjectEvidence[];
  counter_evidence: Record<string, unknown>;
}

// ============================================
// Knowledge Types (match backend model)
// ============================================

export interface KnowledgeEntry {
  id: string;
  category: 'bid' | 'creative' | 'audience' | 'market' | 'keyword' | 'product' | 'monitor' | string;
  title: string;
  description: string;
  trigger_condition: string;
  action: string;
  platform: string;
  market: string;
  priority: 'high' | 'medium' | 'low';
  activation_mode: 'immediate' | 'gradual' | 'record_only';
  source: 'manual' | 'ai_learned';
  status: 'active' | 'candidate' | 'archived';
  skill_file: string;
  usage_count: number;
  created_at: string;
  updated_at?: string;
  promoted_at?: string;
  promoted_by?: string;
  // 全链路核心字段
  scope: 'offsite' | 'onsite' | 'fullchain';
  placement?: string;
  landing_type?: string;
  funnel_stage?: string;
  // 规则型字段
  object_level?: string;
  object_account_ids?: string;
  object_tag?: string;
  scope_tag?: string;
  tag_new_old?: string;
  tag_bid_strategy?: string;
  tag_creative_type?: string;
  tag_funnel?: string;
  tag_series_type?: string;
  tag_attribution?: string;
  tag_event?: string;
  tag_product_category?: string;
  tag_material_size?: string;
  tag_landing_page?: string;
  tag_audience_type?: string;
  tag_extra?: string;
  objective_type?: string;
  metric_name?: string;
  trigger_window?: string;
  threshold_expression?: string;
  diagnostic_tags?: string[];
  metric_conditions?: MetricCondition[];
  applicable_tags?: Record<string, Record<string, string[]>>;
  counter_conditions?: string[];
  action_type?: string;
  action_value?: string;
  expected_impact?: string;
  // 治理字段
  review_status?: string;
  enabled?: boolean;
  maintainer?: string;
  import_batch_no?: string;
  confidence?: number;
  remark?: string;
  // AI召回字段
  keywords?: string;
  question_examples?: string;
  answer_template?: string;
}

export interface KnowledgeListResponse {
  data: KnowledgeEntry[];
  total: number;
  page: number;
  size: number;
}

export interface ListKnowledgeParams {
  brand_id?: string;
  site_id?: string;
  category?: string;
  status?: string;
  source?: string;
  platform?: string;
  market?: string;
  search?: string;
  scope?: string;
  landing_type?: string;
  funnel_stage?: string;
  enabled?: string;
  page?: number;
  size?: number;
}

export interface CreateKnowledgeRequest {
  brand_id?: string;
  site_id?: string;
  category: string;
  title: string;
  description: string;
  trigger_condition?: string;
  action?: string;
  platform?: string;
  market?: string;
  priority?: string;
  activation_mode?: string;
  source?: string;
  skill_file?: string;
  // 全链路核心字段
  scope?: string;
  placement?: string;
  landing_type?: string;
  funnel_stage?: string;
  // 规则型字段
  object_level?: string;
  object_account_ids?: string;
  object_tag?: string;
  scope_tag?: string;
  tag_new_old?: string;
  tag_bid_strategy?: string;
  tag_creative_type?: string;
  tag_funnel?: string;
  tag_series_type?: string;
  tag_attribution?: string;
  tag_event?: string;
  tag_product_category?: string;
  tag_material_size?: string;
  tag_landing_page?: string;
  tag_audience_type?: string;
  tag_extra?: string;
  objective_type?: string;
  metric_name?: string;
  trigger_window?: string;
  threshold_expression?: string;
  diagnostic_tags?: string[];
  metric_conditions?: MetricCondition[];
  applicable_tags?: Record<string, Record<string, string[]>>;
  counter_conditions?: string[];
  action_type?: string;
  action_value?: string;
  expected_impact?: string;
  // 治理字段
  review_status?: string;
  enabled?: boolean;
  maintainer?: string;
  import_batch_no?: string;
  confidence?: number;
  remark?: string;
  // AI召回字段
  keywords?: string;
  question_examples?: string;
  answer_template?: string;
}

export interface UpdateKnowledgeRequest {
  category?: string;
  title?: string;
  description?: string;
  trigger_condition?: string;
  action?: string;
  platform?: string;
  market?: string;
  priority?: string;
  activation_mode?: string;
  skill_file?: string;
  // 全链路核心字段
  scope?: string;
  placement?: string;
  landing_type?: string;
  funnel_stage?: string;
  // 规则型字段
  object_level?: string;
  object_account_ids?: string;
  object_tag?: string;
  scope_tag?: string;
  tag_new_old?: string;
  tag_bid_strategy?: string;
  tag_creative_type?: string;
  tag_funnel?: string;
  tag_series_type?: string;
  tag_attribution?: string;
  tag_event?: string;
  tag_product_category?: string;
  tag_material_size?: string;
  tag_landing_page?: string;
  tag_audience_type?: string;
  tag_extra?: string;
  objective_type?: string;
  metric_name?: string;
  trigger_window?: string;
  threshold_expression?: string;
  diagnostic_tags?: string[];
  metric_conditions?: MetricCondition[];
  applicable_tags?: Record<string, Record<string, string[]>>;
  counter_conditions?: string[];
  action_type?: string;
  action_value?: string;
  expected_impact?: string;
  // 治理字段
  review_status?: string;
  enabled?: boolean;
  maintainer?: string;
  confidence?: number;
  remark?: string;
  // AI召回字段
  keywords?: string;
  question_examples?: string;
  answer_template?: string;
}

// ============================================
// Knowledge API
// ============================================

export const knowledgeApi = {
  list: (params?: ListKnowledgeParams) =>
    api.get<KnowledgeListResponse>('/knowledge', { params }),

  create: (data: CreateKnowledgeRequest) =>
    api.post<KnowledgeEntry>('/knowledge', data),

  get: (id: string) =>
    api.get<KnowledgeEntry>(`/knowledge/${id}`),

  update: (id: string, data: UpdateKnowledgeRequest) =>
    api.put<KnowledgeEntry>(`/knowledge/${id}`, data),

  delete: (id: string) =>
    api.delete<void>(`/knowledge/${id}`),

  promote: (id: string) =>
    api.put<KnowledgeEntry>(`/knowledge/${id}/promote`),

  archive: (id: string) =>
    api.put<KnowledgeEntry>(`/knowledge/${id}/archive`),
};

export const adTagApi = {
  getOptions: (site: string, key: string) =>
    api.get<AdTagOption[]>('/ad-tags/options', { params: { site, key } }),
};
